import authService from './auth.service';
import type { SecretGrant, SecretGrantPayload } from '../types/secret.types';

const API_URL = '/api/v1';

export interface SecretTemplateRecord {
  id: number;
  template_id?: number;
  template_code: string;
  name: string;
  owner_type: string;
  owner_id: number;
  owner_full_name?: string | null;
  tags?: Record<string, unknown> | null;
  version: number | null;
  status?: string | null;
  is_deleted?: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  my_grant?: SecretGrant | null;
}

export interface CreateSecretRecordPayload {
  template_code: string;
  name: string;
  data: Record<string, unknown>;
  owner_type?: string;
  owner_id?: number;
  tags?: Record<string, unknown> | null;
}

// Объект в файловом хранилище (MinIO). Эндпоинт GET /files/list.
export interface StorageFile {
  key: string;
  size: number;
  last_modified: string | null;
  etag: string | null;
}

class StorageService {
  async assignCustomer(secretId: number, customerId: number): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/secrets/${secretId}/assign-customer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customer_id: customerId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось привязать клиента');
    }
  }

  async getUrl(key: string): Promise<string> {
    const url = `${API_URL}/storage/url?key=${encodeURIComponent(key)}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось получить ссылку на файл');
    }
    const data = await res.json();
    return typeof data === 'string' ? data : (data?.url as string);
  }

  async getFileUrl(key: string): Promise<string> {
    const url = `${API_URL}/files/url?key=${encodeURIComponent(key)}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось получить ссылку на файл');
    }
    const data = await res.json();
    return typeof data === 'string' ? data : (data?.url as string);
  }

  /**
   * Загрузить файл в хранилище. Бэк сам добавляет org-префикс к ключу.
   * @returns storage key загруженного файла (например org_42/academy/intro.mp4)
   */
  async uploadFile(file: File, prefix?: string): Promise<string> {
    const url = prefix
      ? `${API_URL}/files/upload?prefix=${encodeURIComponent(prefix)}`
      : `${API_URL}/files/upload`;
    const fd = new FormData();
    fd.append('file', file, file.name);
    const res = await authService.fetchWithAuthForm(url, { method: 'POST', body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить файл');
    }
    const data = await res.json();
    const key = typeof data === 'string' ? data : (data?.key as string | undefined);
    if (!key) {
      throw new Error('Сервер не вернул ключ файла');
    }
    return key;
  }

  /**
   * Список файлов в хранилище. prefix вкладывается внутрь org-префикса бэком.
   */
  async listFiles(prefix?: string): Promise<StorageFile[]> {
    const url = prefix
      ? `${API_URL}/files/list?prefix=${encodeURIComponent(prefix)}`
      : `${API_URL}/files/list`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить список файлов');
    }
    const data = await res.json().catch(() => []);
    if (!Array.isArray(data)) return [];
    return data.map((item: any) => ({
      key: String(item.key ?? ''),
      size: Number(item.size ?? 0),
      last_modified: item.last_modified ?? null,
      etag: item.etag ?? null,
    }));
  }

  async listTemplateRecords(
    templateCode: string,
    params?: {
      skip?: number;
      limit?: number;
      status?: string;
      name?: string;
      customer_full_name?: string;
    }
  ): Promise<{ items: SecretTemplateRecord[]; total: number }> {
    const {
      skip = 0,
      limit = 20,
      status,
      name,
      customer_full_name: customerFullName,
    } = params || {};
    const url = new URL(`${API_URL}/secrets/templates/${encodeURIComponent(templateCode)}/records`, window.location.origin);
    url.searchParams.set('skip', String(skip));
    url.searchParams.set('limit', String(limit));
    const statusParam = typeof status === 'string' ? status.trim() : '';
    if (statusParam) {
      url.searchParams.set('status', statusParam);
    }
    const nameParam = typeof name === 'string' ? name.trim() : '';
    if (nameParam) {
      url.searchParams.set('name', nameParam);
    }
    const customerFullNameParam = typeof customerFullName === 'string' ? customerFullName.trim() : '';
    if (customerFullNameParam) {
      url.searchParams.set('customer_full_name', customerFullNameParam);
    }

    const res = await authService.fetchWithAuth(url.pathname + url.search);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить записи хранилища');
    }
    const data = await res.json().catch(() => ({ items: [], total: 0 }));

    console.log('[storage.service] listTemplateRecords response:', {
      templateCode,
      skip,
      limit,
      responseFormat: Array.isArray(data) ? 'array' : 'object',
      itemsCount: Array.isArray(data) ? data.length : data.items?.length,
      total: data.total,
      page: data.page,
      page_size: data.page_size
    });

    // Поддержка старого формата (массив) и нового (с пагинацией)
    if (Array.isArray(data)) {
      return {
        items: data.map((item: any) => ({
          id: Number(item.id),
          template_id: item.template_id != null ? Number(item.template_id) : undefined,
          template_code: String(item.template_code || templateCode || ''),
          name: String(item.name || ''),
          owner_type: String(item.owner_type || ''),
          owner_id: Number(item.owner_id || 0),
          owner_full_name: item.owner_full_name ?? null,
          tags: item.tags ?? null,
          version: item.version != null ? Number(item.version) : null,
          status: item.status ?? null,
          is_deleted: item.is_deleted ?? null,
          created_at: item.created_at ?? null,
          updated_at: item.updated_at ?? null,
        })) as SecretTemplateRecord[],
        total: data.length,
      };
    }

    const items = Array.isArray(data.items)
      ? data.items.map((item: any) => ({
          id: Number(item.id),
          template_id: item.template_id != null ? Number(item.template_id) : undefined,
          template_code: String(item.template_code || templateCode || ''),
          name: String(item.name || ''),
          owner_type: String(item.owner_type || ''),
          owner_id: Number(item.owner_id || 0),
          owner_full_name: item.owner_full_name ?? null,
          tags: item.tags ?? null,
          version: item.version != null ? Number(item.version) : null,
          status: item.status ?? null,
          is_deleted: item.is_deleted ?? null,
          created_at: item.created_at ?? null,
          updated_at: item.updated_at ?? null,
        })) as SecretTemplateRecord[]
      : [];
    const total = typeof data.total === 'number' ? data.total : items.length;

    return { items, total };
  }

  async createSecretRecord(payload: CreateSecretRecordPayload): Promise<SecretTemplateRecord> {
    const res = await authService.fetchWithAuth(`${API_URL}/secrets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать запись');
    }
    const item = await res.json();
    return {
      id: Number(item.id),
      template_id: item.template_id != null ? Number(item.template_id) : undefined,
      template_code: String(item.template_code || payload.template_code || ''),
      name: String(item.name || ''),
      owner_type: String(item.owner_type || ''),
      owner_id: Number(item.owner_id || 0),
      owner_full_name: item.owner_full_name ?? null,
      tags: item.tags ?? null,
      version: item.version != null ? Number(item.version) : null,
      status: item.status ?? null,
      is_deleted: item.is_deleted ?? null,
      created_at: item.created_at ?? null,
      updated_at: item.updated_at ?? null,
    } as SecretTemplateRecord;
  }

  async getSecretGrants(secretId: number): Promise<SecretGrant[]> {
    const res = await authService.fetchWithAuth(`${API_URL}/secrets/${secretId}/grants`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить гранты секрета');
    }
    const data = await res.json().catch(() => []);
    if (!Array.isArray(data)) return [];
    return data.map((item: any) => ({
      subject_id: Number(item.subject_id),
      subject_full_name: String(item.subject_full_name || ''),
      can_view: Boolean(item.can_view),
      can_update: Boolean(item.can_update),
      can_update_fields: item.can_update_fields !== undefined ? Boolean(item.can_update_fields) : undefined,
      can_share: Boolean(item.can_share),
    })) as SecretGrant[];
  }

  async updateSecretGrants(secretId: number, grants: SecretGrantPayload[]): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/secrets/${secretId}/grants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ grants }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить гранты секрета');
    }
  }
}

const storageService = new StorageService();

export { storageService };
export default storageService;
